import { useEffect, useState } from 'react';
import { get } from 'lodash';
import { IconButton, Tooltip } from '@mui/material';
import { Help } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import { Post, Role } from 'lib/types';
import { User } from 'lib/types/users';
import { CreditsTableView } from 'components/CreditsTableView';
import { AddCredits, useAddCredits } from 'components/AddCredits';
import { HowToCredit } from 'components/HowToCredit/HowToCredit';
import { updateRouterQuery } from 'lib/utils/filterUtils';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { useModal } from 'mui-modal-provider';
import { AutoCreditModal } from 'features/Settings/AccountsTab/AutoCreditModal';
import { userIsPaidTeamMember } from 'lib/helpers';
import { useTeams } from 'lib/contexts/teamsContext';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { C2PAIcon } from 'elements/C2PAIcon';
import { MobileTooltip } from 'components/MobileTooltip';
import { honeybadger } from 'server/honeybadger';
import { getC2paThumbnailUrl } from 'lib/utils/imageUtils';
import { AddCreditButton } from './AddCreditButton';

interface Props {
  post: Post;
  onChange: (message?: string) => void;
}

export const PostCredits = ({
  post,
  onChange,
}: Props) => {
  const currentUser = useCurrentUser();
  const teams = useTeams();

  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();
  const router = useRouter();
  const { addCredits } = useAddCredits();

  const [isAdding, setIsAdding] = useState(false);
  const [isHowToCreditOpen, setIsHowToCreditOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [c2paThumbnail, setC2paThumbnail] = useState<string | undefined>(undefined);

  /**
   * If there is an 'add' in the query,
   * delete it so we exit 'add' mode after a credit is added (or if the user cancels).
   * Specify `useReplace` so we don't change browser query history.
  */
  const deleteAddFromQuery = () => {
    updateRouterQuery(router, { add: [] }, { useReplace: true });
  };

  const save = async (
    user: User, roles: Role[], isPrivate?: boolean,
  ) => {
    try {
      const postingAccount = await addCredits([post.id], user, roles, isPrivate);
      if (postingAccount?.id) {
        const modal = showModal(AutoCreditModal, {
          postingAccountId: postingAccount.id as number,
          network: postingAccount.network,
          username: postingAccount.account,
          autoCredit: {
            role: roles[0],
            roleId: roles[0].id as number,
            userId: user.id,
            postingAccountId: postingAccount.id as number,
          },
          user,
          onAdd: () => {
            modal.destroy();
          },
          onClose: () => {
            modal.destroy();
          },
        });
      }

      deleteAddFromQuery();
      if (onChange) onChange('Credit(s) have been added.');
    } catch (e) {
      const msg = get(e, 'message');
      enqueueSnackbar(`There was an error adding the credit(s): ${msg}`, { variant: 'error' });
    }
  };

  const saveAndClose = async (
    user: User, roles: Role[], isPrivate?: boolean,
  ) => {
    await save(user, roles, isPrivate);
    setShowHint(true);
    setIsAdding(false);
  };

  const cancel = () => {
    deleteAddFromQuery();
    setIsAdding(false);
  };

  useEffect(() => {
    if (!currentUser || (!currentUser.username && !userIsPaidTeamMember(currentUser, teams))) {
      return;
    }

    // If user clicks from +
    setIsAdding(router.query.add === '1');
    // if there are no credits
    if (post?.credits?.length === 0) setIsAdding(true);
  }, [post?.id]);

  useEffect(() => {
    const getC2paInfo = async () => {
      const { createC2pa } = await import('c2pa');

      const c2pa = await createC2pa({
        wasmSrc: '/toolkit_bg.wasm',
        workerSrc: '/c2pa.worker.min.js',
      });

      try {
        const thumbnailUrl = getC2paThumbnailUrl(post.thumbnailUrl);
        const { manifestStore } = await c2pa.read(thumbnailUrl);
        const activeManifest = manifestStore?.activeManifest;
        if (activeManifest) {
          setC2paThumbnail(thumbnailUrl);
          honeybadger.notify(`Post ${post.id} has C2PA manifest!`, 'C2PA Success!');
        } else {
          setC2paThumbnail(undefined);
        }
      } catch (err: any) {
        // noop
        console.error('Error reading image:', err);
      }
    };

    if (currentUser?.id && post?.thumbnailUrl && process.env.NODE_ENV !== 'test') {
      getC2paInfo();
    } else {
      setC2paThumbnail(undefined);
    }
  }, [post.thumbnailUrl, currentUser?.id]);

  return (
    <>
      <div className="flex items-center pt-4">
        <h5 className="font-bold text-2xl mr-2">Credits</h5>
        <Tooltip title="Learn about crediting">
          <IconButton onClick={() => setIsHowToCreditOpen((p) => !p)}>
            <Help color="secondary" />
          </IconButton>
        </Tooltip>
        {c2paThumbnail && (
          <div className="w-1/6">
            <MobileTooltip title="View C2PA credentials">
              <a href={`https://verify.contentauthenticity.org?source=${c2paThumbnail}`} target="_blank" rel="noreferrer">
                <C2PAIcon />
              </a>
            </MobileTooltip>
          </div>
        )}
      </div>
      {isHowToCreditOpen && <HowToCredit onClose={() => setIsHowToCreditOpen(false)} />}
      <CreditsTableView
        credits={post.credits}
        postId={post.id}
        onChange={onChange}
        onEdit={() => setIsAdding(false)}
      />
      {(post.credits.length === 1 && !isAdding && showHint) && (
        <div>
          <ArrowDownwardIcon />
          Add collaborators to give everyone the credit they deserve.
        </div>
      )}
      {isAdding && (
        <div className="border-b">
          <AddCredits
            postId={post.id}
            credits={post.credits}
            onCancel={cancel}
            onSave={saveAndClose}
            onSaveAndNext={save}
          />
        </div>
      )}
      {!isAdding
        && (
          <div className="border-b py-4">
            <AddCreditButton onClick={() => setIsAdding(true)} />
          </div>
        )}
    </>
  );
};
